import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { sar } from "@/components/QuoteCard";

const VAT = 0.15;
const blank = () => ({ desc: "", qty: "1", unit_price: "" });

const priced = (v) => (v == null || v === "" ? "" : String(v));

const defaultItems = (insp, p = {}) => {
  const m = insp.measurements || {};
  const count = Number(m.quantity) || 1;
  const size = m.width || m.height ? ` مقاس ${m.width || "-"}×${m.height || "-"} مم` : "";
  const door = (p.doors || {})[m.door_type];
  const area = (Number(m.width) || 0) * (Number(m.height) || 0) / 1e6;
  const byArea = door?.unit === "m2" && area > 0;
  const items = [{
    desc: `باب ${m.door_type || ""}${size}${byArea ? ` (${count} × ${area.toFixed(2)} م²)` : ""}`.replace(/\s+/g, " ").trim(),
    qty: String(byArea ? Math.round(area * count * 100) / 100 : count), unit_price: priced(door?.price),
  }];
  if (m.device) items.push({ desc: `جهاز ${m.device}`, qty: String(Number(m.device_qty) || 1), unit_price: priced((p.devices || {})[m.device]) });
  (m.extras || []).forEach((x) => items.push({ desc: x, qty: "1", unit_price: priced((p.extras || {})[x]) }));
  return [...items, { desc: "التركيب والتوصيل", qty: "1", unit_price: priced(p.install) }];
};

export const QuoteDialog = ({ open, onClose, insp, onSaved }) => {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState("15");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const q = insp.quote;
    if (q) setItems(q.items.map((i) => ({ desc: i.desc, qty: String(i.qty), unit_price: String(i.unit_price) })));
    else {
      setItems(defaultItems(insp));
      api.get("/prices").then((r) => setItems(defaultItems(insp, r.data))).catch(() => {});
    }
    setDays(String(q?.valid_days || 15));
    setNotes(q?.notes || "");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i, k, v) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const vat = Math.round(subtotal * VAT * 100) / 100;

  const save = async () => {
    const clean = items.filter((i) => i.desc.trim() || i.unit_price);
    if (!clean.length) return toast.error("أضف بنداً واحداً على الأقل");
    if (clean.some((i) => !i.desc.trim() || !(Number(i.qty) > 0) || i.unit_price === "" || Number(i.unit_price) < 0)) return toast.error("أكمل الوصف والكمية والسعر لكل بند");
    setBusy(true);
    try {
      const { data } = await api.put(`/inspections/${insp.id}/quote`, {
        items: clean.map((i) => ({ desc: i.desc.trim(), qty: Number(i.qty), unit_price: Number(i.unit_price) })),
        valid_days: Number(days) || 15, notes,
      });
      onSaved(data);
      toast.success("تم حفظ عرض السعر وإرساله للمندوب");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">{insp.quote ? "تعديل عرض السعر المبدئي" : "إصدار عرض سعر مبدئي"} · {insp.customer.name}</DialogTitle></DialogHeader>
        <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-bold text-slate-500 px-1">
          <span className="col-span-6">الوصف</span><span className="col-span-2">الكمية</span><span className="col-span-3">سعر الوحدة (ر.س)</span>
        </div>
        <div className="space-y-3 sm:space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center border-b sm:border-0 pb-3 sm:pb-0" data-testid={`quote-item-${i}`}>
              <Input value={it.desc} onChange={(e) => set(i, "desc", e.target.value)} placeholder="وصف البند" className="col-span-12 sm:col-span-6" data-testid={`quote-item-desc-${i}`} />
              <Input value={it.qty} onChange={(e) => set(i, "qty", e.target.value)} type="number" min="0" step="any" placeholder="الكمية" dir="ltr" className="col-span-4 sm:col-span-2 text-center" data-testid={`quote-item-qty-${i}`} />
              <Input value={it.unit_price} onChange={(e) => set(i, "unit_price", e.target.value)} type="number" min="0" step="any" placeholder="السعر" dir="ltr" className="col-span-6 sm:col-span-3 text-center" data-testid={`quote-item-price-${i}`} />
              <button onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} disabled={items.length === 1} aria-label="حذف البند" data-testid={`quote-item-remove-${i}`}
                className="col-span-2 sm:col-span-1 h-10 grid place-items-center rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => setItems((xs) => [...xs, blank()])} disabled={items.length >= 50} data-testid="quote-add-item"
          className="h-10 rounded-lg border border-dashed border-slate-300 text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-slate-50"><Plus size={16} />إضافة بند</button>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm font-semibold space-y-1">صلاحية العرض (أيام)
            <Input value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, "").slice(0, 2))} dir="ltr" className="text-center" data-testid="quote-valid-days" />
          </label>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="quote-dialog-totals">
            <p className="flex justify-between"><span>المجموع</span><span className="font-mono">{sar(subtotal)}</span></p>
            <p className="flex justify-between"><span>الضريبة 15%</span><span className="font-mono">{sar(vat)}</span></p>
            <p className="flex justify-between font-bold text-brand-red text-base"><span>الإجمالي</span><span className="font-mono" data-testid="quote-dialog-total">{sar(subtotal + vat)} ر.س</span></p>
          </div>
        </div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000} placeholder="ملاحظات أو شروط (مدة التوريد، الدفعات...)" data-testid="quote-notes" />
        <button onClick={save} disabled={busy} data-testid="quote-save-button"
          className="h-12 rounded-lg bg-brand-navy text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity">
          {busy && <Loader2 size={18} className="animate-spin" />}حفظ وإرسال للمندوب
        </button>
      </DialogContent>
    </Dialog>
  );
};
